import { useCallback, useEffect, useState } from "react";
import { apiFetchOrThrow } from "../api/client";

interface ApiKeyItem {
  id: string;
  name: string;
  prefix: string;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

interface CreatedApiKey extends ApiKeyItem {
  key: string;
}

export function useApiKeys({ active = true }: { active?: boolean } = {}) {
  const [apiKeys, setApiKeys] = useState<ApiKeyItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchKeys = useCallback(async () => {
    try {
      const data = await apiFetchOrThrow<{ apiKeys: ApiKeyItem[] }>(
        "/api-keys",
      );
      setApiKeys(data.apiKeys);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) fetchKeys();
  }, [active, fetchKeys]);

  return { apiKeys, loading, refetch: fetchKeys };
}

export function useCreateApiKey() {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async (name: string): Promise<CreatedApiKey> => {
    setIsPending(true);
    try {
      const data = await apiFetchOrThrow<{ apiKey: CreatedApiKey }>(
        "/api-keys",
        {
          method: "POST",
          body: JSON.stringify({ name }),
        },
      );
      return data.apiKey;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
}

export function useDeleteApiKey() {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async (id: string) => {
    setIsPending(true);
    try {
      await apiFetchOrThrow(`/api-keys/${id}`, { method: "DELETE" });
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
}
